import { fetchWorkingHours, fetchCompanyDetail, fetchNogaDetail } from "./dataFetching";
import { formatValue } from "../helpers";
import { SURVEY_YEAR } from "pages/Evaluations/KeyFigures/kpiConfiguration";

function extractActiveFilters(salaryFilters, filterId, isInitialLoad) {
  const DEFAULT_ITEMS = [];
  if (isInitialLoad) {
    return DEFAULT_ITEMS;
  }
  let filter = salaryFilters.filters.find((e) => e.id === filterId);
  if (filter) {
    let items = filter.items.filter((e) => e.active).map((e) => e.id);
    return items;
  }
  return DEFAULT_ITEMS;
}

export function extractRequestBodyFromFilters(salaryFilters, isInitialLoad) {
  let years = [SURVEY_YEAR]
  const nogaCodes = extractActiveFilters(salaryFilters, "nogaCode", isInitialLoad);
  const officeSizeIds = extractActiveFilters(salaryFilters, "officeSize", isInitialLoad);
  const greaterRegions = extractActiveFilters(salaryFilters, "greaterRegion", isInitialLoad);
  const employmentLevelIds = extractActiveFilters(salaryFilters, "levelId", isInitialLoad);
  const specXfuncIds = extractActiveFilters(salaryFilters, "functionId", isInitialLoad);
  const ageCategories = extractActiveFilters(salaryFilters, "ageCategory", isInitialLoad);
  const sex = extractActiveFilters(salaryFilters, "sex", isInitialLoad);
  let body = {
    years,
    nogaCodes,
    officeSizeIds,
    greaterRegions,
    employmentLevelIds,
    specXfuncIds,
    ageCategories,
    sex,
  };

  return body;
}

function activateDefaultNogaCode(filtersCopy, nogaFilterIndex) {
  const ARCHI_OFFICE_NOGA_CODE = 711101;
  filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
    ...option,
    active: option.id === ARCHI_OFFICE_NOGA_CODE,
  }));
  return filtersCopy;
}

async function setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, companyAccess) {
  let onError = (err) => console.log(err);
  let companyDetail = await fetchCompanyDetail(onError, companyAccess);
  let { nogaId } = companyDetail.data[0];
  let nogaDetails = await fetchNogaDetail(languageKey, nogaId, onError);
  let { nogaCode } = nogaDetails.data;
  if (nogaFilterIndex !== -1) {
    filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
      ...option,
      active: option.id === nogaCode,
    }));
    const noActiveNogas = filtersCopy.filters[nogaFilterIndex].items.every((item) => !item.active);
    // If the company doesn't have noga
    // Activate The Archi office as default selected noga
    if (noActiveNogas) {
      activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
    }
  }
  return filtersCopy;
}

async function setInitialActiveEmployeeFunctions(filtersCopy, languageKey, companyAccess) {
  let onError = (err) => console.log(err);
  let requestBody = extractRequestBodyFromFilters(filtersCopy, false);
  let wagesData = await fetchWorkingHours(languageKey, requestBody, onError, companyAccess);
  let { context } = wagesData.data;
  let nextAvailableFilters = context.nextAvailableFilters;
  let nextAvailableFucntionIds = nextAvailableFilters.functionId.map((e) => e.id);
  let functionIdFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "functionId");
  if (functionIdFilterIndex !== -1) {
    filtersCopy.filters[functionIdFilterIndex].items = filtersCopy.filters[functionIdFilterIndex].items.map((option) => ({
      ...option,
      active: nextAvailableFucntionIds.includes(option.id),
    }));
  }
  return filtersCopy;
}

function setInitialActiveSurveyYear(filtersCopy) {
  let surveyYearsFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "surveyYears");
  let currentYear = new Date().getFullYear();
  filtersCopy.filters[surveyYearsFilterIndex].items = filtersCopy.filters[surveyYearsFilterIndex].items
    .filter((e) => e.id === currentYear)
    .map((e) => ({ ...e, active: true }));
  return filtersCopy;
}

export async function updateFilters(filters, submitedFilters, nextAvailableFilters, isInitialLoad, isMasterKeyValid, languageKey) {
  let filtersCopy = { ...filters };

  if (isInitialLoad) {
    filtersCopy.allAvailabeFilters = nextAvailableFilters;
  }

  filtersCopy.filters = filtersCopy.filters.map((filter) => {
    let filterCopy = { ...filter };
    filterCopy.items = filtersCopy.allAvailabeFilters[filter.id].map((item) => {
      let dropDownOption = {};
      if (filter.id === "surveyYears") {
        dropDownOption = { id: item, label: item, active: submitedFilters[filter.id].includes(item) };
        return dropDownOption;
      }
      let filterIds = nextAvailableFilters[filter.id].map((e) => e.id);
      dropDownOption = {
        id: item.id,
        label: item.value,
        name: item.value,
        active: submitedFilters[filter.id].includes(item.id),
        disabled: !filterIds.includes(item.id),
      };
      return dropDownOption;
    });

    return filterCopy;
  });
  let nogaFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "nogaCode");
  if (isInitialLoad && isMasterKeyValid) {
    filtersCopy = await setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, isMasterKeyValid);
    filtersCopy = await setInitialActiveEmployeeFunctions(filtersCopy, languageKey, isMasterKeyValid);
    filtersCopy = setInitialActiveSurveyYear(filtersCopy);
    return { ...filtersCopy, newest: true, isInitialLoad: false };
  } else if (isInitialLoad && !isMasterKeyValid) {
    filtersCopy = activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
    filtersCopy = await setInitialActiveEmployeeFunctions(filtersCopy, languageKey, isMasterKeyValid);
    filtersCopy = setInitialActiveSurveyYear(filtersCopy);
    return { ...filtersCopy, newest: true, isInitialLoad: false };
  }

  return { ...filtersCopy, newest: false, isInitialLoad: false };
}

const employmentTypes = [
  { id: 5, name: "officeOwner" },
  { id: 4, name: "partner" },
  { id: 3, name: "employee" },
  { id: 2, name: "trainee" },
  { id: 1, name: "intern" },
];

function buildRowObject(metricData, targetData, subgroup, metricLabels, fallbackValue) {
  const row = {
    group: targetData?.levelName ?? "",
    subgroup,
    referenceAverages: [],
  };
  // Index by label for fast + safe lookup
  const byLabel = new Map(
    (Array.isArray(metricData) ? metricData : []).filter((x) => x && typeof x.valueName === "string").map((x) => [x.valueName, x])
  );
  // Fill keys in a stable order
  for (const label of metricLabels) {
    const obj = byLabel.get(label);
    row[label] = [obj?.mean ?? fallbackValue];
  }
  return row;
}
function getAverageObject(averageData, targetData, metricLabels) {
  return buildRowObject(averageData, targetData, "average", metricLabels, 0);
}
function getOwnObject(ownData, targetData, metricLabels) {
  return buildRowObject(ownData, targetData, "ownValue", metricLabels, 0);
}
// ---------------------------
// Charts functions

export function parseChartData(data) {
  if (data.length === 0) {
    return [];
  }
  let chartsData = employmentTypes.map((employmentType) => {
    let targetData = data.find((d) => d.levelId === employmentType.id);
    if (targetData) {
      let { hourlyAggregationsPerYear } = targetData;
      hourlyAggregationsPerYear = hourlyAggregationsPerYear[0];
      const { metrics, companyValues } = hourlyAggregationsPerYear;
      const averageData = metrics.stackedBar;
      const ownData = companyValues?.stackedBar || [];
      const labels = averageData.map((m) => m.valueName);
      const averageObject = getAverageObject(averageData, targetData, labels);
      const ownObject = getOwnObject(ownData, targetData, labels);

      return [averageObject, ownObject];
    }
    return [];
  });
  return chartsData.flat();
}

// End of Chart functions
// ---------------------------

// ---------------------------
// Table functions
function getLabel(labels, key, fallback) {
  return labels?.[key] ?? fallback ?? key;
}

function generateCellPair(level, valueName, isMasterKeyValid) {
  let averageCell = {};
  let ownCell = {};

  const { hourlyAggregationsPerYear } = level;
  const metricsTable = hourlyAggregationsPerYear?.[0]?.metrics?.table ?? [];

  const averageClasses = isMasterKeyValid ? "average" : "average without-ownvalue";
  const object = metricsTable.find((e) => e.valueName === valueName);

  if (object) {
    averageCell = { value: formatValue(object.mean), className: averageClasses };
    if (object.percentage != null) {
      averageCell.value = `${averageCell.value} | ${object.percentage.toFixed(1)} %`;
    }
  } else {
    averageCell = { value: "-", className: averageClasses };
  }
  const companyValuesTable = hourlyAggregationsPerYear?.[0]?.companyValues?.table ?? [];
  const ownValueObject = companyValuesTable.find((e) => e.valueName === valueName);

  if (ownValueObject) {
    ownCell = { value: formatValue(ownValueObject.mean), className: "own-value" };
    if (ownValueObject.percentage != null) {
      ownCell.value = `${ownCell.value} | ${ownValueObject.percentage.toFixed(1)} %`;
    }
  } else {
    ownCell = { value: "-", className: "own-value" };
  }

  return isMasterKeyValid ? [averageCell, ownCell] : [averageCell];
}

function generateRowsWithSubrows(data, rowsWithSubrowsValueNames, labels, isMasterKeyValid) {
  const resultObject = { mainRow: [], subRows: [] };
  const [sectionKey, valueNames] = Object.entries(rowsWithSubrowsValueNames)[0];
  const sectionTitle = getLabel(labels, sectionKey, rowsWithSubrowsValueNames?.[sectionKey]);
  resultObject.mainRow.push({ value: sectionTitle, className: "left-align bold-cell" });

  data.forEach((level) => {
    const cells = generateCellPair(level, sectionKey, isMasterKeyValid);
    resultObject.mainRow.push(...(isMasterKeyValid ? cells : [cells[0]]));
  });

  // subrows
  valueNames.forEach((valueName) => {
    const subRow = [];
    const firstCell = { value: getLabel(labels, valueName), className: "left-align" };
    subRow.push(firstCell);

    data.forEach((level) => {
      const cells = generateCellPair(level, valueName, isMasterKeyValid);
      subRow.push(...cells);
    });
    resultObject.subRows.push(subRow);
  });

  return resultObject;
}

function generateRowsWithoutSubrows(data, rowsWithoutSubrowsValueNames, labels, isMasterKeyValid) {
  let rows = rowsWithoutSubrowsValueNames.map((valueName) => {
    let resultObject = { mainRow: [], subRows: [] };
    resultObject.mainRow.push({ value: getLabel(labels, valueName), className: "left-align bold-cell" });
    data.forEach((level) => {
      let cells = generateCellPair(level, valueName, isMasterKeyValid);
      resultObject.mainRow.push(...cells);
    });
    return resultObject;
  });
  return rows;
}

export function generateTableCells(data, isMasterKeyValid, labels) {
  // rows without subrows
  const rowsWithoutSubrowsValueNames = [
    "count_people",
    "count_fte",
    "count_female_employees",
    "count_male_employees",
    "yearly_nominal_time",
    // "vacation",
    "yearly_should_time",
    //"over_under_time",
    "yearly_presence_time",
  ];
  const rowWithoutSubrowsValueNames = ["order_presence_time"];

  const absenceValueNames = {
    absences: ["sickness", "military", "maternity", "public_offices", "vocational_school"],
  };

  const attendanceTimeValueNames = {
    non_order_presence_time: ["education", "management", "ads", "edv", "contest", "others"],
  };

  const rows = generateRowsWithoutSubrows(data, rowsWithoutSubrowsValueNames, labels, isMasterKeyValid);
  const absenceRow = generateRowsWithSubrows(data, absenceValueNames, labels, isMasterKeyValid);
  const row = generateRowsWithoutSubrows(data, rowWithoutSubrowsValueNames, labels, isMasterKeyValid);
  const attendanceRow = generateRowsWithSubrows(data, attendanceTimeValueNames, labels, isMasterKeyValid);

  rows.push(absenceRow, ...row, attendanceRow);
  return rows;
}

// End of Table functions
// ---------------------------
